import { useId } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiErrorState } from '@/components/ApiErrorState';
import { useActionError } from '@/components/useActionError';
import { useToast } from '@/components/useToast';
import { useCanWrite } from '@/auth/authHooks';
import { StatusChip } from '@/components/status/StatusChip';
import { formatInstantOrDash } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { deliveryEventLabel, formatCountOrDash } from './settingsLabels';
import { useCrm } from './useCrm';

export function IntegrationsSection() {
  const { t } = useTranslation();
  const locale = useLocale();
  const showToast = useToast();
  const canWrite = useCanWrite();
  const describe = useActionError();
  const headingId = useId();
  const { status, data, error, reload, resync, resyncing } = useCrm();
  const deliveries = data?.lastDeliveries ?? [];
  const missing = data?.missingFields ?? [];
  const notConfigured = data?.status === 'NOT_CONFIGURED' || data?.configured === false;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { sm: 'flex-start' }, justifyContent: 'space-between', mb: 0.5 }}
      >
        <Box>
          <Typography variant="h3" component="h2" id={headingId}>
            {t('settings.integrations.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('settings.integrations.lead')}
          </Typography>
        </Box>
        {canWrite ? (
          <Button
            variant="outlined"
            disabled={resyncing || status === 'loading'}
            onClick={() => {
              void resync()
                .then(() => showToast(t('settings.integrations.toast.resynced')))
                .catch((err: unknown) => showToast(describe(err).message, 'error'));
            }}
          >
            {t('settings.integrations.resync')}
          </Button>
        ) : null}
      </Stack>
      {status === 'loading' && !data ? (
        <Box role="status" aria-label={t('common.loading')} sx={{ mt: 2 }}>
          <Skeleton variant="rounded" height={160} />
        </Box>
      ) : null}
      {status === 'error' ? (
        <Box sx={{ mt: 2 }}>
          <ApiErrorState error={error} onRetry={reload} />
        </Box>
      ) : null}
      {data ? (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusChip domain="crm" status={data?.status} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('settings.integrations.checkedAt', {
                when: formatInstantOrDash(data.statusCheckedAt, locale, t('settings.unavailable')),
              })}
            </Typography>
          </Stack>
          {notConfigured ? (
            <Typography variant="body2">
              {t('settings.integrations.configureIn', {
                where: data.configureIn ?? t('settings.unavailable'),
              })}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
            <Typography variant="body2">
              {t('settings.integrations.pending', {
                count: formatCountOrDash(data.pending, locale),
              })}
            </Typography>
            <Typography variant="body2">
              {t('settings.integrations.failed', {
                count: formatCountOrDash(data.failed, locale),
              })}
            </Typography>
            <Typography variant="body2">
              {t('settings.integrations.sentLast24h', {
                count: formatCountOrDash(data.sentLast24h, locale),
              })}
            </Typography>
          </Stack>
          {missing.length > 0 ? (
            <Alert severity="warning">
              {t('settings.integrations.missingFields', { fields: missing.join(', ') })}
            </Alert>
          ) : null}
          <Paper variant="outlined" sx={{ overflow: 'auto' }}>
            <Table size="small" aria-labelledby={headingId}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('settings.integrations.col.event')}</TableCell>
                  <TableCell>{t('settings.integrations.col.status')}</TableCell>
                  <TableCell align="right">{t('settings.integrations.col.attempts')}</TableCell>
                  <TableCell>{t('settings.integrations.col.sentAt')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>{t('settings.integrations.emptyDeliveries')}</TableCell>
                  </TableRow>
                ) : (
                  deliveries.map((row) => {
                    const node = <StatusChip domain="delivery" status={row.status} />;
                    return (
                      <TableRow key={row.id ?? `${row.event}-${row.sentAt}-${row.userId}`}>
                        <TableCell component="th" scope="row">
                          {deliveryEventLabel(row.event)}
                        </TableCell>
                        <TableCell>{node}</TableCell>
                        <TableCell align="right">
                          {formatCountOrDash(row.attempts, locale)}
                        </TableCell>
                        <TableCell>{formatInstantOrDash(row.sentAt, locale, t('settings.unavailable'))}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      ) : null}
    </Paper>
  );
}
